import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowRight, FaServer, FaHeart, FaCode, FaBrain } from 'react-icons/fa';
import FoundingStory from '../assets/images/FoundingStory.png';

/* ── Intersection observer hook ───────────────────────────── */
function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

/* ── Count-up hook ─────────────────────────────────────────── */
function useCountUp(target, duration = 1400, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    const isNumeric = /^\d+$/.test(String(target));
    if (!isNumeric) { setCount(target); return; }
    let startTime = null;
    const num = parseInt(target, 10);
    const step = (ts) => {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * num));
      if (progress < 1) requestAnimationFrame(step);
      else setCount(num);
    };
    requestAnimationFrame(step);
  }, [start, target, duration]);
  return count;
}

/* ── Stat data ─────────────────────────────────────────────── */
const stats = [
  { raw: '5', suffix: '+', label: 'Years Experience', gradient: 'gradient-text' },
  { raw: '10', suffix: '+', label: 'Projects Shipped', gradient: 'gradient-text-warm' },
  { raw: '100', suffix: '%', label: 'Client Satisfaction', gradient: 'gradient-text' },
  { raw: '100', suffix: '%', label: 'On-Time Delivery', gradient: 'gradient-text-warm' },
];

/* ── Values data ───────────────────────────────────────────── */
const values = [
  {
    icon: FaServer,
    heading: 'Scalable Architecture',
    body: 'We engineer systems designed to grow. From day one, we focus on preventing technical debt so your product scales seamlessly.',
    color: 'var(--accent)',
    bg: 'rgba(37, 99, 235, 0.08)',
    border: 'rgba(37, 99, 235, 0.2)',
  },
  {
    icon: FaHeart,
    heading: 'Client-Obsessed Delivery',
    body: 'Customer satisfaction is our north star. We align completely with your vision and treat every launch with end-to-end commitment.',
    color: 'var(--color-purple)',
    bg: 'rgba(124, 58, 237, 0.08)',
    border: 'rgba(124, 58, 237, 0.2)',
  },
  {
    icon: FaCode,
    heading: 'Production-Grade Code',
    body: 'High-quality, clean, and maintainable code is our defining principle. Senior engineers, industry best practices, rigorous reviews.',
    color: 'var(--color-pink)',
    bg: 'rgba(236, 72, 153, 0.08)',
    border: 'rgba(236, 72, 153, 0.2)',
  },
  {
    icon: FaBrain,
    heading: 'AI-Powered Affordability',
    body: 'We build AI-integrated solutions that deliver maximum long-term value at a smart price. Automation is at the core of how we work.',
    color: 'var(--color-orange)',
    bg: 'rgba(249, 115, 22, 0.08)',
    border: 'rgba(249, 115, 22, 0.2)',
  },
];

/* ── Animated Stat Cell ────────────────────────────────────── */
function StatCell({ stat, visible, index }) {
  const isNumeric = /^\d+$/.test(String(stat.raw));
  const count = useCountUp(stat.raw, 1400, visible && isNumeric);
  const display = isNumeric ? `${count}${stat.suffix}` : stat.raw;

  return (
    <div
      className={`text-center py-8 relative anim-hidden anim-delay-${index + 1} ${visible ? 'anim-visible' : ''}`}
    >
      {index > 0 && (
        <div className="hidden md:block absolute left-0 top-[20%] bottom-[20%] w-px"
             style={{ background: 'var(--section-divider)' }} />
      )}
      <div className="display-md mb-2">
        <span className={stat.gradient}>{display}</span>
      </div>
      <p className="text-sm font-medium tracking-wide" style={{ color: 'var(--text-secondary)' }}>
        {stat.label}
      </p>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════ */

export default function AboutUs() {
  const [heroRef, heroVisible] = useInView(0.1);
  const [storyRef, storyVisible] = useInView(0.1);
  const [statsRef, statsVisible] = useInView(0.2);
  const [valuesRef, valuesVisible] = useInView(0.1);
  const [ctaRef, ctaVisible] = useInView(0.1);

  return (
    <div style={{ background: 'var(--bg-primary)' }}>

      {/* ════════════════════════════════════════
          CINEMATIC HERO SECTION
      ════════════════════════════════════════ */}
      <section
        ref={heroRef}
        className="relative min-h-[75vh] flex flex-col justify-center px-6 pt-28 pb-20 md:pt-36 md:pb-24 max-w-7xl mx-auto overflow-hidden"
      >
        {/* Background orbs */}
        <div className="absolute -top-40 -left-32 w-[600px] h-[600px] rounded-full pointer-events-none"
             style={{ background: 'radial-gradient(circle, rgba(37,99,235,0.07), transparent 70%)', filter: 'blur(80px)' }} />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full pointer-events-none"
             style={{ background: 'radial-gradient(circle, rgba(124,58,237,0.06), transparent 70%)', filter: 'blur(80px)' }} />

        <div className="relative z-10 max-w-5xl">
          {/* Eyebrow */}
          <div className={`flex items-center gap-3 mb-8 anim-hidden ${heroVisible ? 'anim-visible' : ''}`}>
            <div className="h-px w-8 bg-slate-400/40" />
            <span className="text-[10px] font-black uppercase tracking-[0.25em] text-slate-400">About Bombay Coding Company</span>
          </div>

          {/* Headline */}
          <h1
            className={`font-black leading-[1.05] tracking-tight mb-8 anim-hidden anim-delay-1 ${heroVisible ? 'anim-visible' : ''}`}
            style={{ fontSize: 'clamp(2.8rem, 7vw, 5.5rem)', color: 'var(--text-primary)' }}
          >
            We don't just code —<br />
            <span className="gradient-text-purple">we craft the future.</span>
          </h1>

          {/* Sub tagline */}
          <p
            className={`text-lg leading-relaxed max-w-2xl mb-10 anim-hidden anim-delay-2 ${heroVisible ? 'anim-visible' : ''}`}
            style={{ color: 'var(--text-secondary)' }}
          >
            Born in Mumbai in 2022, Bombay Coding Company is a tightly-knit team of developers, designers, 
            and strategists wired for building world-class digital products. We're engineers at heart — obsessed 
            with precision, performance, and impact.
          </p>

          {/* Year badge + CTA */}
          <div className={`flex flex-wrap items-center gap-5 anim-hidden anim-delay-3 ${heroVisible ? 'anim-visible' : ''}`}>
            <div className="flex items-center gap-2 px-4 py-2 rounded-full border text-xs font-bold"
                 style={{ borderColor: 'var(--border-color)', color: 'var(--text-secondary)', background: 'var(--bg-card)' }}>
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              Est. 2022 · Mumbai
            </div>
            <Link
              to="/contactus"
              className="spatial-btn-tech group text-xs tracking-wider inline-flex items-center gap-2 !py-3 !px-6 rounded-xl font-bold relative overflow-hidden"
            >
              <span className="spatial-btn-tech-bg" />
              <span className="spatial-btn-tech-shimmer" />
              <span className="relative z-10 flex items-center gap-2">
                Start a Project <FaArrowRight size={10} className="group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-6 left-6 hidden md:flex items-center gap-2 text-slate-400">
          <div className="w-4 h-7 rounded-full border-2 border-slate-300 dark:border-zinc-600 flex items-start justify-center pt-1.5">
            <div className="w-1 h-2 rounded-full bg-slate-400"
                 style={{ animation: 'scrollDot 1.8s ease-in-out infinite' }} />
          </div>
          <span className="text-[9px] font-bold uppercase tracking-widest">Scroll to explore</span>
        </div>
        <style>{`@keyframes scrollDot { 0%,100%{transform:translateY(0);opacity:1} 60%{transform:translateY(6px);opacity:0.3} }`}</style>
      </section>

      {/* ════════════════════════════════════════
          FOUNDING STORY
      ════════════════════════════════════════ */}
      <section
        ref={storyRef}
        className="border-t py-20 md:py-28"
        style={{ background: 'var(--bg-surface)', borderColor: 'var(--section-divider)' }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row items-center gap-14 lg:gap-20">
            {/* Text */}
            <div className={`lg:w-1/2 anim-hidden ${storyVisible ? 'anim-visible' : ''}`}>
              <p className="eyebrow mb-4">Our Story</p>
              <h2 className="display-md mb-8" style={{ color: 'var(--text-primary)' }}>
                Built from a{' '}
                <span className="gradient-text-warm">late-night idea.</span>
              </h2>
              <div className="space-y-5 text-[15px] leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                <p>
                  Bombay Coding Company was born out of a shared obsession with technology and building things that matter.
                  We are engineers from Pune University — hostel mates who spent countless late-nights debugging, building
                  side projects, and dreaming bigger. In 2022, that shared hunger turned into a company.
                </p>
                <p>
                  From those hostel room beginnings, we've grown into a focused team of developers, designers, and
                  strategists. Our commitment to excellence and our unyielding drive to push the boundaries of what's
                  possible have been the driving forces behind every product we ship.
                </p>
              </div>

              {/* Vision & Mission inline */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-10">
                <div className="p-5 rounded-2xl border" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>
                  <h4 className="font-black text-sm mb-2 gradient-text-warm">Our Vision</h4>
                  <p className="text-xs leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                    To be at the forefront of the digital revolution, continuously setting new standards of excellence
                    where technology seamlessly integrates with everyday life.
                  </p>
                </div>
                <div className="p-5 rounded-2xl border" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>
                  <h4 className="font-black text-sm mb-2 gradient-text">Our Mission</h4>
                  <p className="text-xs leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                    To empower businesses through cutting-edge technology solutions that drive growth, enhance
                    efficiency, and create lasting digital value.
                  </p>
                </div>
              </div>
            </div>

            {/* Image */}
            <div className={`lg:w-1/2 anim-hidden anim-delay-2 ${storyVisible ? 'anim-visible' : ''}`}>
              <div className="relative">
                <div className="absolute -inset-4 rounded-3xl opacity-30 blur-2xl"
                     style={{ background: 'radial-gradient(circle, var(--accent), rgba(124,58,237,0.3))' }} />
                <img
                  src={FoundingStory}
                  alt="Founding story"
                  className="relative rounded-2xl shadow-2xl shadow-black/10 w-full"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════
          ANIMATED STATS STRIP
      ════════════════════════════════════════ */}
      <section
        ref={statsRef}
        className="py-16 border-t border-b"
        style={{
          background: 'var(--bg-card)',
          borderColor: 'var(--section-divider)',
        }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-0">
            {stats.map((s, i) => (
              <StatCell key={i} stat={s} visible={statsVisible} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════
          VALUES BENTO GRID
      ════════════════════════════════════════ */}
      <section
        ref={valuesRef}
        className="section-padding"
        style={{ background: 'var(--bg-primary)' }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className={`text-center mb-16 anim-hidden ${valuesVisible ? 'anim-visible' : ''}`}>
            <p className="eyebrow mb-3">Why We Build</p>
            <h2 className="display-md" style={{ color: 'var(--text-primary)' }}>
              Powered by <span className="gradient-text-purple">Principle</span>
            </h2>
            <p className="subheadline mt-4 max-w-lg mx-auto">
              Four core beliefs that guide every line of code, every design decision, and every client conversation.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {values.map((v, i) => (
              <div
                key={i}
                className={`group relative flex flex-col p-8 rounded-2xl border transition-all duration-500
                           hover:-translate-y-1.5 hover:shadow-xl
                           anim-hidden anim-delay-${i + 1} ${valuesVisible ? 'anim-visible' : ''}`}
                style={{ background: 'var(--bg-card)', borderColor: 'var(--border-color)' }}
              >
                {/* Top accent line on hover */}
                <div
                  className="absolute top-0 left-0 right-0 h-[2px] rounded-t-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{ background: v.color }}
                />

                {/* Icon */}
                <div
                  className="w-12 h-12 rounded-2xl flex items-center justify-center mb-6 transition-all duration-300 group-hover:scale-110 shadow-sm"
                  style={{ background: v.bg, color: v.color, border: `1.5px solid ${v.border}` }}
                >
                  <v.icon size={20} />
                </div>

                <h3 className="text-lg font-bold mb-2 tracking-tight" style={{ color: 'var(--text-primary)' }}>
                  {v.heading}
                </h3>
                <p className="text-xs leading-relaxed flex-grow" style={{ color: 'var(--text-secondary)' }}>
                  {v.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════
          CTA CLOSER
      ════════════════════════════════════════ */}
      <section
        ref={ctaRef}
        className="section-padding"
        style={{ background: 'var(--bg-surface)', borderTop: '1px solid var(--section-divider)' }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div
            className={`relative rounded-3xl px-8 py-14 md:px-14 md:py-20 overflow-hidden border text-center anim-hidden ${ctaVisible ? 'anim-visible' : ''}`}
            style={{ background: 'var(--cta-bg)', borderColor: 'var(--cta-border)' }}
          >
            {/* Background orb glows */}
            <div className="absolute top-0 left-1/4 -translate-y-1/2 w-[350px] h-[350px] rounded-full blur-[120px] pointer-events-none"
                 style={{ background: 'var(--accent-glow)' }} />
            <div className="absolute bottom-0 right-0 translate-x-1/4 translate-y-1/4 w-[250px] h-[250px] rounded-full blur-[90px] pointer-events-none"
                 style={{ background: 'rgba(168, 85, 247, 0.12)' }} />

            <div className="relative z-10">
              <p className="text-xs font-bold uppercase tracking-[0.2em] mb-4" style={{ color: 'var(--accent)' }}>
                Let's Build Together
              </p>
              <h2 className="text-3xl md:text-5xl font-black text-white mb-5 leading-tight tracking-tight">
                Have an idea?<br />
                <span className="gradient-text-warm">Let's engineer it.</span>
              </h2>
              <p className="text-sm md:text-base mb-10 max-w-xl mx-auto leading-relaxed"
                 style={{ color: 'rgba(255,255,255,0.6)' }}>
                Tell us what you want to build. We'll respond within 24 hours with a clear, no-obligation technical roadmap tailored to your vision.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4">
                <Link
                  to="/contactus"
                  className="spatial-btn-tech group text-xs tracking-wider inline-flex items-center gap-2 !py-3 !px-8 rounded-xl font-bold relative overflow-hidden"
                >
                  <span className="spatial-btn-tech-bg" />
                  <span className="spatial-btn-tech-shimmer" />
                  <span className="relative z-10 flex items-center gap-2">
                    Start a Conversation <FaArrowRight size={10} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
                <Link
                  to="/portfolio"
                  className="spatial-btn-secondary !text-white/80 hover:!text-white"
                >
                  View Our Work
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
